import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  width?: number | string;
  height?: number | string;
  placeholder?: string;
  onLoad?: () => void;
  onError?: () => void;
  lazy?: boolean;
}

const ImageContainer = styled.div<{ $isLoaded: boolean; $isInView: boolean }>`
  position: relative;
  overflow: hidden;
  background-color: ${props => props.$isLoaded ? 'transparent' : '#f5f5f5'};
  transition: opacity 0.3s ease;
  opacity: ${props => props.$isLoaded ? 1 : 0.7};
`;

const Image = styled.img<{ $isLoaded: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s ease;
  opacity: ${props => props.$isLoaded ? 1 : 0};
`;

const Placeholder = styled.div<{ $show: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  color: #999;
  font-size: 2rem;
  opacity: ${props => props.$show ? 1 : 0};
  transition: opacity 0.3s ease;
  pointer-events: none;
`;

const ErrorMessage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f8f8;
  color: #666;
  font-size: 0.9rem;
  text-align: center;
  padding: 1rem;
`;

export const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  className,
  style,
  width,
  height,
  placeholder = '📷',
  onLoad,
  onError,
  lazy = true
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(!lazy);
  const [optimizedSrc, setOptimizedSrc] = useState('');
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Generate optimized image sources
  const generateOptimizedSrc = (originalSrc: string): string => {
    if (!originalSrc) return '';
    
    // If already a full URL, return as is
    if (originalSrc.startsWith('http')) {
      return originalSrc;
    }

    // For article images, WebP optimization is handled by nginx
    return originalSrc;
  };

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || isInView) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [lazy, isInView]);

  // Update optimized source when src changes
  useEffect(() => {
    setOptimizedSrc(generateOptimizedSrc(src));
    setIsLoaded(false);
    setHasError(false);
  }, [src]);

  // Handle image load
  const handleLoad = () => {
    setIsLoaded(true);
    setHasError(false);
    if (onLoad) {
      onLoad();
    }
  };

  // Handle image error
  const handleError = () => {
    setHasError(true);
    setIsLoaded(false);
    if (onError) {
      onError();
    }
  };

  return (
    <ImageContainer
      ref={containerRef}
      className={className}
      style={{ width, height, ...style }}
      $isLoaded={isLoaded}
      $isInView={isInView}
    >
      {isInView && optimizedSrc && !hasError && (
        <Image
          ref={imgRef}
          src={optimizedSrc}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          $isLoaded={isLoaded}
          loading={lazy ? 'lazy' : 'eager'}
        />
      )}
      
      <Placeholder $show={!isLoaded && !hasError}>
        {placeholder}
      </Placeholder>
      
      {hasError && (
        <ErrorMessage>
          画像を読み込めませんでした
        </ErrorMessage>
      )}
    </ImageContainer>
  );
};

export default LazyImage; 